import React, { useRef, KeyboardEvent } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

interface Option {
  value: string;
  label: string;
}

interface KeyboardNavigableSelectProps {
  options: Option[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  triggerStyle?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  itemStyle?: React.CSSProperties;
}

/**
 * A Select component that supports keyboard navigation between options
 * without opening the dropdown
 */
const KeyboardNavigableSelect: React.FC<KeyboardNavigableSelectProps> = ({
  options,
  value,
  onValueChange,
  placeholder,
  disabled,
  className,
  triggerClassName,
  triggerStyle,
  contentStyle,
  itemStyle
}) => {
  const selectRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Only handle arrow up/down keys
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') {
      return;
    }
    
    // If the dropdown is open, let the default behavior handle it
    const popoverOpen = selectRef.current?.getAttribute('data-state') === 'open';
    if (popoverOpen) {
      return;
    }
    
    // Prevent default behavior (which would open the dropdown)
    e.preventDefault();
    
    // Find the current index
    const currentIndex = options.findIndex(option => option.value === value);
    
    // If not found or no options, do nothing
    if (currentIndex === -1 || options.length === 0) {
      return;
    }
    
    // Calculate the new index with wrap-around
    let newIndex;
    if (e.key === 'ArrowDown') {
      newIndex = (currentIndex + 1) % options.length;
    } else {
      newIndex = (currentIndex - 1 + options.length) % options.length;
    }
    
    // Call onValueChange with the new value
    onValueChange(options[newIndex].value);
  };

  return (
    <div 
      ref={selectRef} 
      onKeyDown={handleKeyDown}
      className={className}
    >
      <Select
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
      >
        <SelectTrigger className={triggerClassName} style={triggerStyle}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent style={contentStyle}>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value} style={itemStyle}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default KeyboardNavigableSelect;